import { Duration, Effect, Schedule, Stream, pipe } from 'effect'
import type { EffectOutput, ProcessMessage } from './process'

/** Equality-safe execution and reconciliation inputs, compared with `Object.is`. */
export type TaskArgument = string | number | boolean | bigint | null | undefined
export type TaskArguments = readonly TaskArgument[]
export type TaskKey = Exclude<TaskArgument, null | undefined>
/** Structured child-instance path. Scope distinguishes otherwise identical Task definitions. */
export type TaskScope = readonly string[]

/**
 * Executable work that may emit zero or more values.
 *
 * A Task may emit once through Effect or repeatedly through Stream. Effect v4
 * makes that distinction explicit, so the runtime normalizes at the execution
 * boundary instead of making every caller wrap one-shot work in Stream.
 */
export type TaskProgram<Output, Error = never, Requirements = never> =
  | Effect.Effect<Output, Error, Requirements>
  | Stream.Stream<Output, Error, Requirements>

export function taskProgramStream<Output, Error, Requirements>(
  program: TaskProgram<Output, Error, Requirements>
): Stream.Stream<Output, Error, Requirements> {
  return Stream.isStream(program) ? program : Stream.fromEffect(program)
}

type MessageFromTaskOutput<Output> = Output extends null | undefined
  ? never
  : Output extends void
  ? never
  : Output extends ReadonlyArray<infer Item>
  ? Item extends ProcessMessage
    ? Item
    : never
  : Output extends ProcessMessage
  ? Output
  : never

type TaskDefinitionIdentity = {
  readonly _tag: 'TaskDefinition'
  /** Diagnostic only. Reconciliation uses definition object identity, not this number. */
  readonly diagnosticId: number
}

export type TaskDefinition<
  Arguments extends readonly unknown[],
  Message extends ProcessMessage,
  Requirements,
  Error = never,
  ReconciliationArguments extends TaskArguments = TaskArguments
> = TaskDefinitionIdentity & {
  readonly run: (...args: Arguments) => TaskProgram<EffectOutput<Message>, Error, Requirements>
  (...args: Arguments): TaskDeclaration<Message, Error, Requirements, ReconciliationArguments>
}

/**
 * Inert desired work returned from `tasks(state)`.
 *
 * Constructing a declaration starts nothing. The store reconciles its
 * `(scope, definition)` slot and arguments against the currently running
 * declaration, then starts, retains, or stops the program.
 */
export type TaskDeclaration<
  Message extends ProcessMessage = ProcessMessage,
  Error = never,
  Requirements = unknown,
  Arguments extends TaskArguments = TaskArguments
> = {
  readonly _tag: 'TaskDeclaration'
  readonly definition: TaskDefinitionIdentity
  readonly arguments: Arguments
  readonly scope: TaskScope
  readonly program: () => TaskProgram<EffectOutput<Message>, Error, Requirements>
}

/**
 * A declaration as the store's runtime handles it: typed work can no longer
 * fail, but the runtime still reports a program that dies.
 */
export type RuntimeTaskDeclaration = TaskDeclaration<ProcessMessage, unknown, unknown>

/** The Message emitted by a Task definition or declaration. */
export type TaskMessage<Task> = Task extends TaskDefinition<any, infer Message, any, any>
  ? Message
  : Task extends TaskDeclaration<infer Message, any, any, any>
  ? Message
  : never

let nextDiagnosticId = 0

/**
 * Creates a module-scoped Task definition.
 *
 * The definition object's identity is its reconciliation slot. Create it once
 * at module scope, never inside `tasks(state)` or `update`; a definition
 * created per call is a fresh slot and continually restarts. TypeScript cannot
 * enforce this placement rule.
 *
 * Calling the definition returns an inert declaration. Its arguments are both
 * execution inputs and session identity, compared with `Object.is`, which is
 * why the public argument domain is limited to equality-safe primitives.
 * Supplying `key` allows independently scoped instances for a dynamic
 * collection; the key becomes identity while the full arguments start that
 * instance.
 * Like Elm's `Sub`, a Task cannot fail: its program turns every outcome into a
 * message, or declares a failure a defect with `Effect.orDie`. A defect is
 * reported by the store and latches the Task until its declaration changes.
 */
function define<
  const Arguments extends TaskArguments,
  Output extends EffectOutput<ProcessMessage>,
  Requirements
>(
  run: (...args: Arguments) => TaskProgram<Output, never, Requirements>
): TaskDefinition<Arguments, MessageFromTaskOutput<Output>, Requirements, never, Arguments>
function define<
  const Arguments extends readonly unknown[],
  const Key extends TaskKey,
  Output extends EffectOutput<ProcessMessage>,
  Requirements
>(
  run: (...args: Arguments) => TaskProgram<Output, never, Requirements>,
  options: { readonly key: (...args: Arguments) => Key }
): TaskDefinition<Arguments, MessageFromTaskOutput<Output>, Requirements, never, readonly [Key]>
function define(
  run: (...args: any[]) => TaskProgram<EffectOutput<ProcessMessage>, any, any>,
  options?: { readonly key: (...args: any[]) => TaskKey }
): TaskDefinition<any, ProcessMessage, any, any> {
  let definition: TaskDefinition<any, ProcessMessage, any, any>

  const declare = (...args: any[]) => {
    const key = options?.key(...args)
    return {
      _tag: 'TaskDeclaration' as const,
      definition,
      arguments: key == undefined ? args : [key],
      scope: key == undefined ? [] : [`key:${typeof key}:${String(key)}`],
      program: () => definition.run(...args)
    }
  }

  definition = Object.assign(declare, {
    _tag: 'TaskDefinition' as const,
    diagnosticId: ++nextDiagnosticId,
    run
  })

  return definition
}

/**
 * Repeats one complete program immediately and then at the requested spacing.
 *
 * The interval starts after the program finishes, so a slow iteration delays
 * the next start instead of overlapping it. Outputs and failures keep their
 * ordinary Stream semantics; interruption stops both the current iteration
 * and every future one.
 */
function periodic(interval: Duration.Input) {
  return <Output, Error, Requirements>(
    program: TaskProgram<Output, Error, Requirements>
  ): Stream.Stream<Output, Error, Requirements> =>
    Stream.repeat(taskProgramStream(program), Schedule.spaced(interval))
}

function map<Output, Mapped>(mapOutput: (output: Output, index: number) => Mapped) {
  return <Error, Requirements>(
    program: TaskProgram<Output, Error, Requirements>
  ): Stream.Stream<Mapped, Error, Requirements> =>
    taskProgramStream(program).pipe(Stream.map(mapOutput))
}

/**
 * The constrained UI-facing subset of Effect Stream.
 *
 * These operators retain their Effect/Stream semantics. In particular,
 * recovery handlers return another Task program; Task does not introduce a
 * second, Message-returning recovery dialect.
 */
export const Task = {
  define,
  pipe,
  succeed: Stream.succeed,
  map,
  catchTags: Stream.catchTags,
  periodic
}

function liftTaskOutput<ChildMessage extends ProcessMessage, ParentMessage extends ProcessMessage>(
  output: ChildMessage | readonly ChildMessage[] | null | void,
  lift: (message: ChildMessage) => ParentMessage
): EffectOutput<ParentMessage> {
  if (output == null) {
    return null
  }
  if (Array.isArray(output)) {
    return output.map(lift)
  }
  return lift(output as ChildMessage)
}

/**
 * Re-parents a child declaration without changing its definition or arguments.
 *
 * Preserving those identities is what lets child composition add namespace
 * and message lifting without accidentally restarting the underlying session.
 */
export function scopeTask<
  const Scope extends TaskScope,
  ChildMessage extends ProcessMessage,
  const ParentMessage extends ProcessMessage,
  Error,
  Requirements,
  Arguments extends TaskArguments
>(
  scope: Scope,
  declaration: TaskDeclaration<ChildMessage, Error, Requirements, Arguments>,
  lift: (message: ChildMessage) => ParentMessage
): TaskDeclaration<ParentMessage, Error, Requirements, Arguments> {
  return {
    ...declaration,
    scope: [...scope, ...declaration.scope],
    program: () =>
      taskProgramStream(declaration.program()).pipe(
        Stream.map((output) => liftTaskOutput(output, lift))
      )
  }
}

export function sameTaskScope(left: TaskScope, right: TaskScope) {
  if (left.length != right.length) {
    return false
  }

  for (let index = 0; index < left.length; index++) {
    if (left[index] != right[index]) {
      return false
    }
  }

  return true
}

export function sameTaskArguments(left: TaskArguments, right: TaskArguments) {
  if (left.length != right.length) {
    return false
  }

  for (let index = 0; index < left.length; index++) {
    if (!Object.is(left[index], right[index])) {
      return false
    }
  }

  return true
}

/**
 * Slot identity is `(scope, definition)`. Arguments select the session within
 * that slot and are compared separately by the reconciler.
 */
export function sameTaskSlot(left: RuntimeTaskDeclaration, right: RuntimeTaskDeclaration) {
  if (!sameTaskScope(left.scope, right.scope)) {
    return false
  }

  return left.definition == right.definition
}

export function taskDisplayName(task: RuntimeTaskDeclaration) {
  // Display names are diagnostic labels; they never participate in reconciliation.
  return [...task.scope, `task#${task.definition.diagnosticId}`].join('/')
}
